import { TableColumn, formatDate } from '@veracity/vui'

import {
  BaseScenarioTimeSeriesChartItemSimple,
  CanUsScenarioTimeSeriesChartItemSimple,
  SpainScenarioTimeSeriesChartItemSimple,
  SystemConfigurations
} from '@/interfaces'
import { formatters } from '@/utils'

export const useScenarioResultsTimeSeriesTableConfig = (
  data: BaseScenarioTimeSeriesChartItemSimple[],
  systemConfiguration?: SystemConfigurations
) => {
  if (data.length === 0) {
    return { columns: [], rows: [] }
  }

  if ('aFrred' in data[0]) {
    return getSpainTableConfig(
      data as SpainScenarioTimeSeriesChartItemSimple[],
      systemConfiguration
    )
  }

  return getCanUsTableConfig(data as CanUsScenarioTimeSeriesChartItemSimple[])
}

const getSpainTableConfig = (
  data: SpainScenarioTimeSeriesChartItemSimple[],
  systemConfiguration?: SystemConfigurations
) => {
  let columns: TableColumn[] = [
    { title: 'Timestamp', field: 'timestampString', minW: '15em' },
    { title: 'DAM Energy', field: 'dae' },
    { title: 'aFRR Energy Up', field: 'aFrreu' },
    { title: 'aFRR Energy Down', field: 'aFrred' },
    { title: 'aFRR Power Up', field: 'aFrrpu' },
    { title: 'aFRR Power Down', field: 'aFrrpd' },
    { title: 'mFRR Energy Up', field: 'mFrreu' },
    { title: 'mFRR Energy Down', field: 'mFrred' },
    { title: 'aFRR Energy Up Price', field: 'priceAFrreu' },
    { title: 'aFRR Energy Down Price', field: 'priceAFrred' },
    { title: 'mFRR Energy Up Price', field: 'priceMfrreu' },
    { title: 'mFRR Energy Down Price', field: 'priceMfrred' },
    { title: 'aFRR Up Energy Ratio', field: 'regUpEnToCap' },
    { title: 'aFRR Down Energy Ratio', field: 'regDownEnToCap' },
    { title: 'SOC', field: 'lEsEnd' },
    { title: 'DAM Charge', field: 'gGesDa' },
    { title: 'DAM Discharge', field: 'gEsgDa' }
  ]

  switch (systemConfiguration) {
    case SystemConfigurations.Standalone:
      columns = [...columns]
      break
    case SystemConfigurations.SolarPV:
      columns = [
        ...columns,
        { title: 'PV Grid Export', field: 'gPvg' },
        { title: 'BESS Charge', field: 'gPvesGrid' },
        { title: 'BESS Charge (from Clipped Energy)', field: 'gPvesClip' },
        { title: 'PV Clipped Energy', field: 'gpvClipDump' },
        { title: 'Economic Curtailment', field: 'gPvc' }
      ]
      break
    case SystemConfigurations.SolarPVOnSharedBus:
      columns = [
        ...columns,
        { title: 'PV Grid Export', field: 'gPvg' },
        { title: 'BESS Charge', field: 'gPvesGrid' },
        { title: 'Economic Curtailment', field: 'gPvc' }
      ]
      break
    default:
      columns = []
      break
  }

  return {
    columns: columns,
    rows: data.map(x => ({
      timestampString: formatDate(x.timestamp, 'DD MMM, HH:MM'),
      dae: formatters.number(x.dae),
      aFrreu: formatters.number(x.aFrreu),
      aFrred: formatters.number(x.aFrred),
      aFrrpu: formatters.number(x.aFrrpu),
      aFrrpd: formatters.number(x.aFrrpd),
      mFrreu: formatters.number(x.mFrreu),
      mFrred: formatters.number(x.mFrred),
      priceAFrreu: formatters.number(x.priceAFrreu),
      priceAFrred: formatters.number(x.priceAFrred),
      priceMfrreu: formatters.number(x.priceMfrreu),
      priceMfrred: formatters.number(x.priceMfrred),
      regUpEnToCap: formatters.number(x.regUpEnToCap),
      regDownEnToCap: formatters.number(x.regDownEnToCap),
      lEsEnd: formatters.number(x.lEsEnd),
      gGesDa: formatters.number(x.gGesDa),
      gEsgDa: formatters.number(x.gEsgDa),
      gPvg: formatters.number(x.gPvg),
      gPvesGrid: formatters.number(x.gPvesGrid),
      gPvesClip: formatters.number(x.gPvesClip),
      gpvClipDump: formatters.number(x.gpvClipDump),
      gPvc: formatters.number(x.gPvc)
    }))
  }
}

const getCanUsTableConfig = (
  data: CanUsScenarioTimeSeriesChartItemSimple[]
) => {
  return {
    columns: [
      { title: 'Timestamp', field: 'timestampString', minW: '15em' },
      { title: 'RTE', field: 'rte' },
      { title: 'DAE', field: 'dae' },
      { title: 'Net Reg Energy', field: 'netRegEnergy' },
      { title: 'ES Output POI', field: 'esOutputPoi' },
      {
        title: 'Reg Down while discharge',
        field: 'regulationDownCapacityWhileDischarging'
      },
      {
        title: 'Reg Up while discharge',
        field: 'regulationUpCapacityWhileDischarging'
      },
      {
        title: 'Reg Down while charge',
        field: 'regulationDownCapacityWhileCharging'
      },
      {
        title: 'Reg Up while discharge',
        field: 'regulationUpCapacityWhileCharging'
      },
      {
        title: 'SR while discharge',
        field: 'spinngingReserveWhileDischarging'
      },
      { title: 'SR while charge', field: 'spinngingReserveWhileCharging' },
      { title: 'Price DAE', field: 'dayAheadEnergyPrice' },
      { title: 'Price RTE', field: 'realTimeEnergyPrice' },
      {
        title: 'Price - Reg Up Capacity',
        field: 'regulationUpCapacityPrice'
      },
      {
        title: 'Price - Reg Down Capacity',
        field: 'regulationDownCapacityPrice'
      },
      {
        title: 'Price - Spinning Reserve Capacity',
        field: 'spinningReservePrice'
      },
      { title: 'ES Charge', field: 'esCharging' },
      { title: 'ES Discharge', field: 'esDischarging' },
      { title: 'ES SoE end of Interval', field: 'esSoeEndOfInterval' },
      { title: 'Max SOE', field: 'maxSoe' }
    ],
    rows: data.map(x => ({
      ...x,
      timestampString: formatDate(x.timestamp, 'DD MMM, HH:MM'),
      rte: formatters.number(x.rte),
      dae: formatters.number(x.dae),
      netRegEnergy: formatters.number(x.netRegEnergy),
      esOutputPoi: formatters.number(x.esOutputPoi),
      regulationDownCapacityWhileDischarging: formatters.number(
        x.regulationDownCapacityWhileDischarging
      ),
      regulationUpCapacityWhileDischarging: formatters.number(
        x.regulationUpCapacityWhileDischarging
      ),
      regulationDownCapacityWhileCharging: formatters.number(
        x.regulationDownCapacityWhileCharging
      ),
      regulationUpCapacityWhileCharging: formatters.number(
        x.regulationUpCapacityWhileCharging
      ),
      spinngingReserveWhileDischarging: formatters.number(
        x.spinngingReserveWhileDischarging
      ),
      spinngingReserveWhileCharging: formatters.number(
        x.spinngingReserveWhileCharging
      ),
      dayAheadEnergyPrice: formatters.number(x.dayAheadEnergyPrice),
      realTimeEnergyPrice: formatters.number(x.realTimeEnergyPrice),
      regulationUpCapacityPrice: formatters.number(x.regulationUpCapacityPrice),
      regulationDownCapacityPrice: formatters.number(
        x.regulationDownCapacityPrice
      ),
      spinningReservePrice: formatters.number(x.spinningReservePrice),
      esCharging: formatters.number(x.esCharging),
      esDischarging: formatters.number(x.esDischarging),
      esSoeEndOfInterval: formatters.number(x.esSoeEndOfInterval),
      maxSoc: formatters.number(x.maxSoe)
    }))
  }
}
